import { GetDistances, LatLng } from 'src/components/GoogleMaps/GoogleMapsTypes';

/**
 * Library for google maps api
 *
 * types for module
 * promise wrappers for used api calls
 */

export enum MapTypeId {
    /**
     * This map type displays a transparent layer of major streets on satellite
     * images.
     */
    HYBRID = 'hybrid',

    /**
     * This map type displays a normal street map.
     */
    ROADMAP = 'roadmap',

    /**
     * This map type displays satellite images.
     */
    SATELLITE = 'satellite',

    /**
     * This map type displays maps with physical features such as terrain and
     * vegetation.
     */
    TERRAIN = 'terrain',
}

export interface MapOptions extends google.maps.MapOptions {
    initialCenter: string | google.maps.LatLng | google.maps.LatLngLiteral | null;
}

interface GetAddressCoordinatesProps {
    address: string;
    geocoder: google.maps.Geocoder;
}

/**
 * Get coordinates from address string
 * @param props
 * @returns object
 */
export function getAddressCoordinates({ address, geocoder }: GetAddressCoordinatesProps): Promise<LatLng> {
    return new Promise((resolve, reject) => {
        geocoder.geocode(
            {
                address,
            },
            (results, status) => {
                if (status === google.maps.GeocoderStatus.OK) {
                    if (results) {
                        const coordinates = results[0].geometry.location;
                        resolve({ lat: coordinates.lat(), lng: coordinates.lng() });
                    } else {
                        reject(`Google Maps Geocoder response was null: ${status}`);
                    }
                } else if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
                    reject('No matching results. Please try another postcode or town name.');
                } else {
                    reject(`Google Maps Geocoder failed: ${status}`);
                }
            },
        );
    });
}

/**
 * Calculates the distance in miles / km to the address searched for
 * Result gets appended to the individual location object
 *
 * @param props
 * @returns array
 */
function getDistances({ lat, lng, locations }: GetDistances) {
    const pi = Math.PI;
    const R = 6371; // equatorial radius

    return locations.map((location) => {
        const lat2 = location.latitude;
        const lon2 = location.longitude;

        const chLat = lat2 - lat;
        const chLon = lon2 - lng;

        const dLat = chLat * (pi / 180);
        const dLon = chLon * (pi / 180);

        const rLat1 = lat * (pi / 180);
        const rLat2 = lat2 * (pi / 180);

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(rLat1) * Math.cos(rLat2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return {
            ...location,
            distance_miles: (R * c) / 1.609, // Dividing this by 1.609 to go from KM to Miles
            distance_km: R * c,
        };
    });
}

/**
 * Sorts the locations with the closest showing first
 *
 * @param lat
 * @param lng
 * @param locations
 * @returns array
 */
export function sortByDistance({ lat, lng, locations }: GetDistances) {
    return getDistances({ lat, lng, locations }).sort((a, b) => Math.min(a.distance_km) - Math.min(b.distance_km));
}
